import axios from "axios";
import HostService from "../../../services/host.service";
import AuthService from "../../../services/auth.service";

/**
 * Servicio para el endpoint /peticiones
 * API documentada en ../api/docs/api/peticiones.md
 */
class PeticionesService {

    /**
     * GET /peticiones - lista peticiones con filtro opcional por usuario_id
     * @param {Object} params - Parámetros opcionales
     * @param {number} params.usuarioId - Filtrar por ID de usuario
     * @returns {Promise<Array>} Array de peticiones
     */
    static async list(params = {}) {
        // URLSearchParams serializa undefined y null como las cadenas "undefined" y
        // "null", que viajarían como un filtro con aspecto válido. Se descartan antes.
        const filtros = Object.fromEntries(
            Object.entries(params).filter(([, valor]) => valor !== undefined && valor !== null && valor !== "")
        );
        const queryString = new URLSearchParams(filtros).toString();
        const url = HostService.getHost() + 'peticiones' + (queryString ? `?${queryString}` : '');

        return axios
            .get(url, AuthService.setToken())
            .then((res) => {
                // La API retorna { data: [...], message: "..." }
                const items = res.data?.data ?? [];
                return Array.isArray(items) ? items : [];
            })
            .catch((e) => { console.log(e.message); throw e; });
    }

    /**
     * GET /peticiones?usuario_id=<actual> - lista peticiones del usuario autenticado
     * DECISION: delega a list con el filtro usuario_id para no duplicar lógica
     * @returns {Promise<Array>} Array de peticiones del usuario actual
     */
    static async listMias() {
        const usuarioId = AuthService.getCurrentUser()?.id;

        // Sin usuario no hay a quién acotar el listado, y pedirlo sin filtro devolvería
        // las peticiones de TODOS: preferible fallar que enseñar datos de otros
        if (usuarioId == null) {
            throw new Error("No hay sesión de usuario activa.");
        }

        return PeticionesService.list({ usuarioId });
    }

    /**
     * POST /peticiones - crea una nueva petición para el usuario autenticado
     * El backend infiere usuario_id del JWT
     * @param {Object} peticion - Datos de la petición (referencia, unidades, numeroAlbaran)
     * @returns {Promise<Object>} Petición creada
     */
    static async create(peticion) {
        return axios
            .post(HostService.getHost() + 'peticiones', peticion, AuthService.setToken())
            .then((res) => res.data?.data ?? res.data)
            .catch((e) => { console.log(e.message); throw e; });
    }

    /**
     * PATCH /peticiones/:id/asignar - el reponedor autenticado se asigna la petición
     * El backend extrae el reponedor_id del JWT
     * @param {number} id - ID de la petición
     * @returns {Promise<Object>} Petición actualizada
     */
    static async asignar(id) {
        return axios
            .patch(HostService.getHost() + 'peticiones/' + encodeURIComponent(id) + '/asignar', {}, AuthService.setToken())
            .then((res) => res.data?.data ?? res.data)
            .catch((e) => { console.log(e.message); throw e; });
    }

    /**
     * PATCH /peticiones/:id/preparado - marca la petición como preparada
     * El backend valida que el reponedor del JWT coincide con el asignado
     * @param {number} id - ID de la petición
     * @returns {Promise<Object>} Petición actualizada
     */
    static async marcarPreparado(id) {
        return axios
            .patch(HostService.getHost() + 'peticiones/' + encodeURIComponent(id) + '/preparado', {}, AuthService.setToken())
            .then((res) => res.data?.data ?? res.data)
            .catch((e) => { console.log(e.message); throw e; });
    }

    /**
     * PATCH /peticiones/:id/desasignar - desasigna la petición del reponedor
     * El backend valida que el reponedor del JWT es el asignado
     * Vuelve el estado a NO_ATENDIDO y limpia reponedor_id y fecha_asignacion
     * @param {number} id - ID de la petición
     * @returns {Promise<Object>} Petición actualizada
     */
    static async desasignar(id) {
        return axios
            .patch(HostService.getHost() + 'peticiones/' + encodeURIComponent(id) + '/desasignar', {}, AuthService.setToken())
            .then((res) => res.data?.data ?? res.data)
            .catch((e) => { console.log(e.message); throw e; });
    }

    /**
     * PATCH /peticiones/:id/traspasar - traspasa la petición del almacén prioritario al resto
     * El backend valida que está en NO_ATENDIDO y asignada al almacén prioritario (03)
     * Consulta stock en vivo y reasigna a otros almacenes con stock
     * @param {number} id - ID de la petición
     * @returns {Promise<Object>} Petición actualizada
     */
    static async traspasar(id) {
        return axios
            .patch(HostService.getHost() + 'peticiones/' + encodeURIComponent(id) + '/traspasar', {}, AuthService.setToken())
            .then((res) => res.data?.data ?? res.data)
            .catch((e) => { console.log(e.message); throw e; });
    }

    /**
     * DELETE /peticiones/:id - elimina una petición propia en estado NO_ATENDIDO
     * El backend valida que el usuario del JWT es el creador y el estado es NO_ATENDIDO
     * @param {number} id - ID de la petición
     * @returns {Promise<Object>} Respuesta de la API
     */
    static async delete(id) {
        return axios
            .delete(HostService.getHost() + 'peticiones/' + encodeURIComponent(id), {
                ...AuthService.setToken()
            })
            .then((res) => res.data)
            .catch((e) => { console.log(e.message); throw e; });
    }

    /**
     * GET /peticiones/:id - obtiene una petición por ID
     * @param {number} id - ID de la petición
     * @returns {Promise<Object>} Petición encontrada
     */
    static async getById(id) {
        return axios
            .get(HostService.getHost() + 'peticiones/' + encodeURIComponent(id), AuthService.setToken())
            .then((res) => res.data?.data ?? res.data)
            .catch((e) => { console.log(e.message); throw e; });
    }

    /**
     * GET /peticiones/albaran/:numeroAlbaran - obtiene peticiones por número de albarán
     * @param {string} numeroAlbaran - Número de albarán
     * @returns {Promise<Array>} Array de peticiones del albarán
     */
    static async listPorAlbaran(numeroAlbaran) {
        return axios
            .get(HostService.getHost() + 'peticiones/albaran/' + encodeURIComponent(numeroAlbaran), AuthService.setToken())
            .then((res) => {
                // La API retorna { data: [...], message: "..." }
                const items = res.data?.data ?? [];
                return Array.isArray(items) ? items : [];
            })
            .catch((e) => { console.log(e.message); throw e; });
    }
}

export default PeticionesService;
